import { Flex, Image } from '@chakra-ui/react';
import { Box, Button, Link, Text } from '@everlywell/ui-kit';
import PromoDiscountPrice from 'components/PricingV2/PromoDiscountPrice';
import React, { useMemo } from 'react';
import { EVERLYWELL_360 } from 'utils/constants/products';
import {
  findActivePromotionForProduct,
  type Promo,
} from 'utils/helpers/activePromoHelpers';

import { sanitizeAgentText } from '../../lib/formatters';
import everlywell360 from './everlywell360.png';

type Everlywell360ProductCardProps = {
  description?: string;
  /**
   * Active promos from `useAdvertisedPromos`. When supplied, the card
   * renders a strikethrough price with discount code (matching the other
   * Eva product cards) instead of the plain "Get started" CTA-only layout.
   */
  advertisedPromos?: Promo[];
};

export default function Everlywell360ProductCard({
  description,
  advertisedPromos,
}: Everlywell360ProductCardProps): React.ReactElement {
  const sanitizedDescription = useMemo(
    () => (description ? sanitizeAgentText(description) : ''),
    [description],
  );

  const activePromo = useMemo(
    () =>
      advertisedPromos?.length
        ? findActivePromotionForProduct(
            EVERLYWELL_360.PRODUCT_ID,
            advertisedPromos,
          )
        : undefined,
    [advertisedPromos],
  );
  return (
    <Link
      href="/everlywell-360-quiz"
      borderRadius="2xl"
      padding={2}
      backgroundColor="tints.cream"
      _hover={{
        textDecoration: 'none',
        backgroundColor: 'tints.darkCream',
        border: '1px solid',
        borderColor: 'tints.white',
        boxShadow: 'lg',
      }}
      border="1px solid"
      borderColor="tints.cream"
      height="100%"
      display="flex"
      flexDirection={['row', 'column']}
      gap={[3, 0]}
      alignItems={['center', 'stretch']}
      width="100%"
    >
      <Flex
        flex={[2, 1]}
        justifyContent="center"
        alignItems="center"
        backgroundColor="tints.lightCream"
        borderRadius="xl"
        height={['100%', '230px']}
        minHeight={['120px', '230px']}
        maxWidth={['126px', 'none']}
        // Without this the image's intrinsic width sets the column's
        // min-content width, so the card's image column comes out wider than
        // the ProductCardV2 cards it sits beside and the titles do not line up.
        minWidth={0}
        padding={[3, 6]}
      >
        <Image
          src={everlywell360}
          alt="Everlywell 360"
          objectFit="contain"
          maxWidth="100%"
          maxHeight={['100px', '185px']}
        />
      </Flex>
      <Flex
        flex={['5', '1']}
        flexDirection="column"
        justifyContent="space-between"
        paddingX={[0, 2]}
        paddingTop={[0, 2]}
        minWidth={0}
      >
        <Box margin={[null, 'auto']} paddingTop={[2, 0]}>
          <Text
            fontSize={['lg', '3xl']}
            color="black"
            fontWeight="medium"
            marginY="2"
            textAlign={['left', 'center']}
          >
            Everlywell 360
          </Text>
          {sanitizedDescription ? (
            <Box
              fontSize="sm"
              color="tints.darkGrey"
              textAlign={['left', 'center']}
              dangerouslySetInnerHTML={{ __html: sanitizedDescription }}
            />
          ) : (
            <Text
              fontSize="sm"
              color="tints.darkGrey"
              textAlign={['left', 'center']}
            >
              83 biomarkers. One test. A lifetime of insights.
            </Text>
          )}
        </Box>
        <Flex
          justifyContent="space-between"
          alignItems="center"
          padding={[null, 2]}
          marginTop={[3, 0]}
          gap={2}
        >
          {activePromo ? (
            <Box flex={1} minWidth={0}>
              <PromoDiscountPrice
                price={EVERLYWELL_360.BASE_PRICE}
                promo={activePromo}
                productId={EVERLYWELL_360.PRODUCT_ID}
                promotionable
                subscribeSelected={false}
                isTextReduced
              />
            </Box>
          ) : (
            <Box />
          )}
          <Button
            height={['32px', '40px']}
            fontSize="md"
            fontWeight="medium"
            size="sm"
            minWidth={['100px', '120px']}
            backgroundColor="eggplant.base"
            color="white"
            _hover={{ backgroundColor: 'violet.dark' }}
            flexShrink={0}
          >
            Get started
          </Button>
        </Flex>
      </Flex>
    </Link>
  );
}
